import { useCallback, useEffect, useMemo, useState } from "react";

const createPageItem = (value) => ({ type: "page", value });
const createEllipsisItem = (direction) => ({ type: "ellipsis", direction });

const getPaginationRange = (currentPage, totalPages, siblingCount = 0) => {
  if (totalPages <= 1) {
    return [createPageItem(1)];
  }

  const totalNumbers = siblingCount * 2 + 5;
  if (totalPages <= totalNumbers) {
    return Array.from({ length: totalPages }, (_, i) => createPageItem(i + 1));
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages);
  const shouldShowLeftDots = leftSibling > 2;
  const shouldShowRightDots = rightSibling < totalPages - 1;

  const range = [];

  if (!shouldShowLeftDots && shouldShowRightDots) {
    const leftItemCount = 3 + 2 * siblingCount;
    for (let i = 1; i <= leftItemCount; i += 1) {
      range.push(createPageItem(i));
    }
    range.push(createEllipsisItem("right"));
    range.push(createPageItem(totalPages));
    return range;
  }

  if (shouldShowLeftDots && !shouldShowRightDots) {
    range.push(createPageItem(1));
    range.push(createEllipsisItem("left"));
    const rightItemCount = 3 + 2 * siblingCount;
    for (let i = totalPages - rightItemCount + 1; i <= totalPages; i += 1) {
      range.push(createPageItem(i));
    }
    return range;
  }

  range.push(createPageItem(1));
  range.push(createEllipsisItem("left"));
  for (let i = leftSibling; i <= rightSibling; i += 1) {
    range.push(createPageItem(i));
  }
  range.push(createEllipsisItem("right"));
  range.push(createPageItem(totalPages));

  return range;
};

export const useTablePagination = ({
  data = [],
  itemsPerPage = 10,
  siblingCount = 0,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = useMemo(
    () => Math.max(Math.ceil(data.length / itemsPerPage), 1),
    [data.length, itemsPerPage],
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedData = useMemo(
    () => data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [currentPage, data, itemsPerPage],
  );

  const paginationRange = useMemo(
    () => getPaginationRange(currentPage, totalPages, siblingCount),
    [currentPage, siblingCount, totalPages],
  );

  const handlePageChange = useCallback(
    (page) => {
      const safePage = Math.min(Math.max(page, 1), totalPages);
      setCurrentPage(safePage);
    },
    [totalPages],
  );

  const handleEllipsisClick = useCallback(
    (direction) => {
      const jump = siblingCount * 2 + 1;
      if (direction === "left") {
        handlePageChange(currentPage - jump);
      } else {
        handlePageChange(currentPage + jump);
      }
    },
    [currentPage, handlePageChange, siblingCount],
  );

  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const showPagination = totalPages > 1 && data.length > 0;

  return {
    currentPage,
    totalPages,
    paginatedData,
    paginationRange,
    showPagination,
    handlePageChange,
    handleEllipsisClick,
    setCurrentPage,
    resetPage,
  };
};
